import { useState, useEffect } from 'react';
import { onAuthStateChange, signInWithGoogle, signOut, createUserProfile, getUserProfile } from '../lib/firebase/services';
import { logSignIn, setAnalyticsUserId, setAnalyticsUserProperties } from '../lib/firebase/analytics';
import type { User as FirebaseUser } from 'firebase/auth';
import type { AuthState } from '../types';

export const useAuth = (): AuthState & {
  signIn: () => Promise<void>;
  logOut: () => Promise<void>;
  isAdmin: boolean;
  isUser: boolean;
  authLoading: boolean;
} => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    userProfile: null,
    loading: true,
    error: null
  });
  const [authLoading, setAuthLoading] = useState(false);

  // Track if we're currently creating a user profile to prevent duplicates
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);

  useEffect(() => {
    let isSubscribed = true; // Flag to prevent state updates after unmount
    
    // Add a small delay to ensure Firebase is initialized
    const timer = setTimeout(() => {
      try {
        const unsubscribe = onAuthStateChange(async (user: FirebaseUser | null) => {
          // Prevent processing if component is unmounted or already creating profile
          if (!isSubscribed || isCreatingProfile) {
            return;
          }
          
          if (user) {
            // Check if user profile exists, create if not
            try {
              let userProfile = await getUserProfile(user.uid);
              
              if (!userProfile && !isCreatingProfile) {
                setIsCreatingProfile(true);
                
                // Create new user profile with default 'user' role
                await createUserProfile({
                  uid: user.uid,
                  email: user.email || '',
                  displayName: user.displayName,
                  photoURL: user.photoURL,
                  role: 'user', // Default role
                  walletBalance: 0,
                  isActive: true // New users are active by default
                });
                
                // Fetch the newly created profile
                userProfile = await getUserProfile(user.uid);
                setIsCreatingProfile(false);
              }
              
              if (isSubscribed) {
                setAuthState({
                  user,
                  userProfile,
                  loading: false,
                  error: null
                });
                
                // Set analytics user properties
                setAnalyticsUserId(user.uid);
                setAnalyticsUserProperties({
                  email: user.email || '',
                  role: userProfile?.role || 'user',
                  displayName: user.displayName || ''
                });
              }
            } catch (error) {
              console.error('Error handling user profile:', error);
              setIsCreatingProfile(false);
              if (isSubscribed) {
                setAuthState({
                  user,
                  userProfile: null,
                  loading: false,
                  error: 'Failed to load user profile'
                });
              }
            }
          } else {
            if (isSubscribed) {
              setAuthState({
                user: null,
                userProfile: null,
                loading: false,
                error: null
              });
            }
          }
        });

        return () => {
          isSubscribed = false;
          unsubscribe();
        };
      } catch (error) {
        console.error('Firebase initialization error:', error);
        if (isSubscribed) {
          setAuthState({
            user: null,
            userProfile: null,
            loading: false,
            error: 'Firebase initialization failed. Please check your configuration.'
          });
        }
      }
    }, 100);

    return () => {
      isSubscribed = false;
      clearTimeout(timer);
    };
  }, [isCreatingProfile]);

  const signIn = async (): Promise<void> => {
    try {
      setAuthLoading(true);
      await signInWithGoogle();
      
      // Log sign-in event
      logSignIn('google');
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Sign in failed'
      }));
    } finally {
      setAuthLoading(false);
    }
  };

  const logOut = async (): Promise<void> => {
    try {
      setAuthLoading(true);
      await signOut();
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Sign out failed'
      }));
    } finally {
      setAuthLoading(false);
    }
  };

  // Helper properties to check user role
  const isAdmin = authState.userProfile?.role === 'admin';
  const isUser = authState.userProfile?.role === 'user';

  return {
    ...authState,
    signIn,
    logOut,
    isAdmin,
    isUser,
    authLoading
  };
};
